import { Box, Button, Heading, Stack, Text } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  showDialog?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Report to Sentry with additional context
    Sentry.withScope((scope) => {
      scope.setContext('errorBoundary', {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
        errorBoundaryProps: this.props,
      });
      scope.setLevel('error');
      scope.setTag('error.boundary', true);

      // Add breadcrumb for error boundary trigger
      scope.addBreadcrumb({
        category: 'error.boundary',
        message: 'Error caught by ErrorBoundary',
        level: 'error',
        data: {
          errorMessage: error.message,
          errorStack: error.stack,
        },
      });

      Sentry.captureException(error);
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Show Sentry error dialog if configured
      if (this.props.showDialog) {
        const eventId = Sentry.lastEventId();
        if (eventId) {
          Sentry.showReportDialog({ eventId });
        }
      }

      // Use custom fallback if provided
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // Default error UI
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          bg="gray.50"
          p={8}
        >
          <Stack gap={4} textAlign="center" maxW="md">
            <Heading size="xl" color="red.500">
              Oops! Something went wrong
            </Heading>
            <Text color="gray.600">
              We've encountered an unexpected error. Our team has been notified and we're working on
              fixing it.
            </Text>
            {import.meta.env.DEV && this.state.error && (
              <Box
                p={4}
                bg="red.50"
                borderRadius="md"
                border="1px solid"
                borderColor="red.200"
                w="full"
              >
                <Text fontSize="sm" fontFamily="mono" color="red.700">
                  {this.state.error.message}
                </Text>
              </Box>
            )}
            <Button colorPalette="blue" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Sentry-enhanced error boundary with profiling
export const SentryErrorBoundary = Sentry.withErrorBoundary(ErrorBoundary, {
  fallback: ({ error, componentStack, resetError }) => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bg="gray.50"
      p={8}
    >
      <Stack gap={4} textAlign="center" maxW="md">
        <Heading size="xl" color="red.500">
          Application Error
        </Heading>
        <Text color="gray.600">An error occurred while loading this page. Please try again.</Text>
        {import.meta.env.DEV && (
          <>
            {error && (
              <Box
                p={4}
                bg="red.50"
                borderRadius="md"
                border="1px solid"
                borderColor="red.200"
                w="full"
              >
                <Text fontSize="sm" fontFamily="mono" color="red.700">
                  {String(error)}
                </Text>
                {componentStack && (
                  <Text fontSize="xs" fontFamily="mono" color="red.600" mt={2}>
                    {componentStack}
                  </Text>
                )}
              </Box>
            )}
          </>
        )}
        <Button colorPalette="blue" onClick={resetError}>
          Try Again
        </Button>
      </Stack>
    </Box>
  ),
  showDialog: true,
  beforeCapture: (scope, error, errorInfo) => {
    scope.setTag('error.boundary.sentry', true);
    if (errorInfo && typeof errorInfo === 'object') {
      scope.setContext('errorInfo', errorInfo as unknown as Record<string, unknown>);
    }
  },
});

// Export both the class component and a Sentry-wrapped version
export default ErrorBoundary;
